// File upload / fetch helpers for /api/v1/files.
//
// The endpoint accepts multipart/form-data with a `file` field. If the
// Worker's R2 binding isn't provisioned yet (`ERR_NOT_IMPLEMENTED`), the
// caller can choose to skip the upload or substitute a data URL.

import { api, extractApiError } from './api-client';

export interface UploadResult {
  url: string;
  key: string;
}

export async function uploadFile(file: File): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<UploadResult>('/files/upload', form, {
    // Let the browser set the multipart boundary automatically.
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** True when the most recent upload failure was the 501 stub (R2 absent). */
export function isStorageNotConfigured(err: unknown): boolean {
  return extractApiError(err).code === 'ERR_NOT_IMPLEMENTED';
}

/** Read a File as a base64 data URL — fallback when R2 isn't set up yet. */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Try R2 first; fall back to a base64 data URL if storage isn't configured.
 * Returns the URL string suitable for stashing into a `*_photo_url` column.
 */
export async function uploadOrDataUrl(file: File): Promise<string> {
  try {
    const out = await uploadFile(file);
    return out.url;
  } catch (e) {
    if (isStorageNotConfigured(e)) {
      return await fileToDataUrl(file);
    }
    throw e;
  }
}
